import { Flex } from 'mds-web-ui'
import { motion } from 'motion/react'
import { darken } from 'polished'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { createGlobalStyle } from 'styled-components'
import { getContrastingBorder } from '../../utils/color'
import { navHeight } from '../TopNav'
import getBrowserScrollbarWidth from '../utils/getScrollbarWidth'
import useCustomScrollbars, {
  scrollbarWidth,
} from '../utils/useCustomScrollbars'
import { Fixed } from './Fixed'
import { CloseLink } from './SidebarCloseLink'

const hasVisibleScrollbars = getBrowserScrollbarWidth() > 0
export const sidebarWidth = 430

const RightBodyMargin = createGlobalStyle`
main {
  padding-right: ${({ show }) =>
    show ? `${sidebarWidth - (hasVisibleScrollbars ? 0 : 15)}px` : 0};
}
`

const bottomBarHeight = 95

export const rightEdgeExtra = 100

const MotionFixed = motion.create(Fixed)

export const Sidebar = ({
  bg = '#000000',
  color = '#ffffff',
  open = true,
  children,
  ...rest
}) => {
  const [showBodyMargin, setShowBodyMargin] = useState(false)

  useEffect(() => {
    if (open) setShowBodyMargin(true)
    else
      setTimeout(() => {
        setShowBodyMargin(false)
      }, 150)
  }, [open])
  const [ref, scrollbarsShown, scrollbarStyles] = useCustomScrollbars(
    children,
    null,
    bg,
  )

  const panelBg = darken(0.05, bg)

  return (
    <MotionFixed
      right={-rightEdgeExtra}
      pr={rightEdgeExtra}
      top={navHeight}
      bottom={0}
      pb={bottomBarHeight}
      width={sidebarWidth + rightEdgeExtra}
      bg={panelBg}
      color={color}
      role="complementary"
      aria-label="Action sidebar"
      inert={open ? null : 'inert'}
      initial={{ x: open ? 0 : '100%' }}
      animate={{
        x: open ? 0 : '100%',
        backgroundColor: panelBg,
      }}
      transition={{
        x: {
          type: 'spring',
          stiffness: 420,
          damping: 22,
          mass: 0.9,
          restDelta: 0.5,
          restSpeed: 10,
        },
        backgroundColor: { duration: 0.25 },
      }}
      style={{
        borderTopLeftRadius: hasVisibleScrollbars ? null : '21px',
        borderBottomLeftRadius: hasVisibleScrollbars ? null : '21px',
        boxShadow: getContrastingBorder(bg),
        overflow: 'hidden',
        '@media screen and (max-width:1224px)': {
          boxShadow: '-2px 0 0 0 rgba(0,0,0,0.1)',
        },
      }}
      {...rest}
    >
      <Flex
        flexDirection="column"
        css={{
          height: '100%',
          overflowY: 'auto',
          ...scrollbarStyles,
        }}
        ref={ref}
        p={30}
        pb={35}
        pr={30 - (scrollbarsShown ? scrollbarWidth : 0)}
      >
        {open ? <CloseLink color={color} bg={darken(0.05, bg)} /> : null}
        {children}
      </Flex>
      <RightBodyMargin show={showBodyMargin} />
    </MotionFixed>
  )
}
Sidebar.propTypes = {
  children: PropTypes.node,
  bg: PropTypes.string,
  color: PropTypes.string,
  open: PropTypes.bool,
}
